/*
 * The app's one header: the page's panel toggle, back / forward, then the
 * tab strip (client groups, "+n", search), then the assistant pill
 * at the far right. Tabs are grouped by client; the group you're in opens into
 * segments where it sits and every other group is a tile, so switching tabs
 * never reorders or resizes anything. The group you leave stays open until
 * the pointer leaves the bar, and anything that does move slides. Case tabs
 * have no assistant pill (the assistant lives in the IDE there).
 */
import {
  closestCenter,
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
  type CollisionDetection,
  type DragEndEvent,
  type Modifier
} from "@dnd-kit/core";
import {
  restrictToHorizontalAxis,
  restrictToParentElement
} from "@dnd-kit/modifiers";
import {
  horizontalListSortingStrategy,
  SortableContext
} from "@dnd-kit/sortable";
import { Search } from "lucide-react";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type FC,
  type KeyboardEvent,
  type MouseEvent
} from "react";
import { useNavigate } from "react-router-dom";

import { useAppHeaderConfig } from "@/components/app-header/AppHeaderContext";
import { HeaderDivider } from "@/components/app-header/AppHeaderShell";
import { HistoryNav } from "@/components/app-header/HistoryNav";
import { Button } from "@/components/ui/button";
import { Kbd } from "@/components/ui/kbd";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from "@/components/ui/tooltip";
import {
  tabIdForScope,
  useAgentAttention
} from "@/features/agentAttention/context/AgentAttentionProvider";
import { groupTone } from "@/features/agentAttention/signalTone";
import type { AttentionItem } from "@/features/agentAttention/types";
import { useRunningTurnsStore } from "@/features/agents/runningTurnsStore";
import { useAgentPanelScope } from "@/features/agents/useAgentPanelScope";
import { useGlobalSearchDialog } from "@/features/globalSearch/GlobalSearchContext";
import { NewCaseDialogHost } from "@/features/newCase";
import { NewClientDialogHost } from "@/features/newClient";
import { useGetUserDetails } from "@/hooks/useGetUserDetails";
import { cn } from "@/lib/utils";

import { useActiveTabRegistration } from "../context/ActiveTabContext";
import { useAppTabsStore } from "../context/appTabsStore";
import {
  groupOf,
  groupState,
  groupTabs,
  lastUsedTab,
  type TabGroup as TabGroupModel
} from "../groups";
import { useFlip } from "../hooks/useFlip";
import { SEGMENT_PREFIX } from "../hooks/useSortableTab";
import { useLingeringGroup } from "../hooks/useLingeringGroup";
import { useTabRouteSync } from "../hooks/useTabRouteSync";
import { IDLE_URGENCY, layoutGroups } from "../layout";
import { canCloseTab } from "../tabRoutes";
import type { AppTab as AppTabModel } from "../types";
import { AssistantCue } from "./AssistantCue";
import { GroupHoverCard } from "./GroupHoverCard";
import { GroupTile } from "./GroupTile";
import { OverflowMenu } from "./OverflowMenu";
import { TabGroup } from "./TabGroup";

const NO_SIGNALS: AttentionItem[] = [];
/** An open group's width before it has been measured. */
const UNMEASURED_GROUP_W = 200;
const DRAG_MODIFIERS: Modifier[] = [
  restrictToHorizontalAxis,
  restrictToParentElement
];
/**
 * Groups and the open group's segments share one drag context, so a drop
 * target only counts when it is in the dragged item's own list.
 */
const sameListCenter: CollisionDetection = (args) => {
  const list = args.active.data.current?.sortable?.containerId as
    | string
    | undefined;
  return closestCenter({
    ...args,
    droppableContainers: args.droppableContainers.filter(
      (c) => c.data.current?.sortable?.containerId === list
    )
  });
};

type Props = {
  className?: string;
  /** Only the active tab and the page's actions: no history, search or other tabs. */
  focused?: boolean;
};

export const AppTabBar: FC<Props> = ({ className, focused = false }) => {
  useTabRouteSync();
  const navigate = useNavigate();
  const tabs = useAppTabsStore((s) => s.tabs);
  const activeId = useAppTabsStore((s) => s.activeId);
  const recency = useAppTabsStore((s) => s.recency);
  const close = useAppTabsStore((s) => s.close);
  const keep = useAppTabsStore((s) => s.keep);
  const moveGroup = useAppTabsStore((s) => s.moveGroup);
  const moveTab = useAppTabsStore((s) => s.moveTab);
  const reg = useActiveTabRegistration();
  const header = useAppHeaderConfig();
  const { byTab } = useAgentAttention();
  const turns = useRunningTurnsStore((s) => s.turns);
  const { setOpen: openSearch } = useGlobalSearchDialog();
  const { agent } = useAgentPanelScope();
  const {
    userDetails: { userType }
  } = useGetUserDetails();

  const regForActive = reg && reg.tabId === activeId ? reg : null;

  const running = useMemo(
    () => new Set(turns.map((t) => tabIdForScope(t.scope.kind, t.scope.id))),
    [turns]
  );
  const signalsFor = useCallback(
    (id: string) => byTab.get(id) ?? NO_SIGNALS,
    [byTab]
  );
  const isRunning = useCallback((id: string) => running.has(id), [running]);
  const urgency = useCallback(
    (group: TabGroupModel) => {
      const { signals, running: working } = groupState(
        group,
        signalsFor,
        isRunning
      );
      const tone = groupTone(signals);
      if (tone === "red") return 0;
      if (tone === "green") return 1;
      return working ? 2 : IDLE_URGENCY;
    },
    [signalsFor, isRunning]
  );
  const groups = useMemo(() => groupTabs(tabs), [tabs]);
  const groupKeys = useMemo(() => groups.map((g) => g.key), [groups]);
  const openKey = groupOf(groups, activeId)?.key ?? null;
  const { lingeringKey, barProps, over, settle } = useLingeringGroup(
    openKey,
    groupKeys
  );

  // The strip's width and each open group's drawn width drive the layout.
  // Groups keep their last measured width, so the one you just left knows
  // its width before it is drawn lingering.
  const stripRef = useRef<HTMLDivElement>(null);
  const [stripW, setStripW] = useState(0);
  const [groupW, setGroupW] = useState<Record<string, number>>({});
  useLayoutEffect(() => {
    const el = stripRef.current;
    if (!el) return undefined;
    const measure = () =>
      setStripW(Math.round(el.getBoundingClientRect().width));
    measure();
    if (typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  useLayoutEffect(() => {
    const els =
      stripRef.current?.querySelectorAll<HTMLElement>("[data-group-mode]") ??
      [];
    const changed: Record<string, number> = {};
    els.forEach((el) => {
      const key = el.dataset.groupKey ?? "";
      const w = Number(el.dataset.flipWidth) || el.offsetWidth;
      if (key && w && Math.abs((groupW[key] ?? 0) - w) > 1) changed[key] = w;
    });
    if (Object.keys(changed).length)
      setGroupW((prev) => ({ ...prev, ...changed }));
  });

  // While the pointer is on the bar, the tiles it drew keep their places, so
  // a signal arriving on a hidden group never swaps a tile out from under a
  // click. Once the pointer leaves, the bar re-picks and useFlip slides it.
  const [drawnKeys, setDrawnKeys] = useState<string[]>([]);
  const layout = layoutGroups({
    groups,
    openKey,
    lingeringKey,
    stripWidth: stripW || Number.POSITIVE_INFINITY,
    openWidth: (openKey && groupW[openKey]) || UNMEASURED_GROUP_W,
    lingeringWidth:
      (lingeringKey && groupW[lingeringKey]) || UNMEASURED_GROUP_W,
    urgency,
    recency,
    sticky: over ? drawnKeys : null
  });
  const drawnId = layout.visibleKeys.join("\n");
  useLayoutEffect(() => {
    setDrawnKeys((prev) =>
      prev.join("\n") === drawnId ? prev : drawnId ? drawnId.split("\n") : []
    );
  }, [drawnId]);
  const shownLingering = layout.lingerFits ? lingeringKey : null;
  useEffect(() => {
    if (lingeringKey && !layout.lingerFits) settle();
  }, [lingeringKey, layout.lingerFits, settle]);

  const visibleKeys = new Set(focused ? [openKey] : layout.visibleKeys);
  const visibleGroups = groups.filter((g) => visibleKeys.has(g.key));
  const hidden = focused ? [] : groups.filter((g) => !visibleKeys.has(g.key));
  const sortableKeys = visibleGroups.map((g) => g.key);
  const { skipNext } = useFlip(stripRef);

  const select = useCallback(
    (tab: AppTabModel) => navigate(tab.route),
    [navigate]
  );
  const closeTab = useCallback(
    (tab: AppTabModel) => {
      if (!canCloseTab(tab.id)) return;
      const next = close(tab.id);
      if (next) navigate(next.route);
    },
    [close, navigate]
  );
  const closeGroup = useCallback(
    (group: TabGroupModel) => {
      let next: AppTabModel | null = null;
      for (const tab of group.tabs)
        if (canCloseTab(tab.id)) next = close(tab.id) ?? next;
      if (next) navigate(next.route);
    },
    [close, navigate]
  );
  const keepTab = useCallback((tab: AppTabModel) => keep(tab.id), [keep]);

  // Rearranging: drag a group along the bar or a tab within its group, or
  // press Ctrl+Shift+Left/Right on a focused one. A tab never leaves its
  // client's group.
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } })
  );
  const [dragging, setDragging] = useState(false);
  const dropped = useRef(false);
  const endDrag = useCallback(() => setDragging(false), []);
  const onDragEnd = useCallback(
    ({ active, over }: DragEndEvent) => {
      setDragging(false);
      // The pointerup that ends a drag is followed by a click on the tab.
      dropped.current = true;
      setTimeout(() => {
        dropped.current = false;
      }, 0);
      const from = String(active.id);
      const to = over ? String(over.id) : null;
      if (!to || to === from) return;
      const fromSeg = from.startsWith(SEGMENT_PREFIX);
      if (fromSeg !== to.startsWith(SEGMENT_PREFIX)) return;
      if (fromSeg) {
        const id = from.slice(SEGMENT_PREFIX.length);
        const overId = to.slice(SEGMENT_PREFIX.length);
        const at = groupOf(groups, id)?.tabs.findIndex((t) => t.id === overId);
        if (at === undefined || at < 0) return;
        skipNext();
        moveTab(id, at);
      } else {
        const at = groups.findIndex((g) => g.key === to);
        if (at < 0) return;
        skipNext();
        moveGroup(from, at);
      }
    },
    [groups, moveGroup, moveTab, skipNext]
  );
  const swallowDropClick = useCallback((e: MouseEvent) => {
    if (!dropped.current) return;
    dropped.current = false;
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const refocus = useRef<{ group?: string; tab?: string } | null>(null);
  useLayoutEffect(() => {
    const want = refocus.current;
    const strip = stripRef.current;
    if (!want || !strip) return;
    refocus.current = null;
    const el = want.tab
      ? Array.from(strip.querySelectorAll<HTMLElement>("[data-tab-id]")).find(
          (n) => n.dataset.tabId === want.tab
        )
      : Array.from(
          strip.querySelectorAll<HTMLElement>("button[data-group-key]")
        ).find((n) => n.dataset.groupKey === want.group);
    (el?.matches("button") ? el : el?.querySelector("button"))?.focus();
  });
  const moveDelta = (e: KeyboardEvent): number => {
    if (!e.ctrlKey || !e.shiftKey || e.altKey || e.metaKey) return 0;
    if (e.key === "ArrowLeft") return -1;
    if (e.key === "ArrowRight") return 1;
    return 0;
  };
  const onGroupMoveKey = (e: KeyboardEvent, key: string) => {
    const delta = moveDelta(e);
    if (!delta) return;
    e.preventDefault();
    e.stopPropagation();
    refocus.current = { group: key };
    moveGroup(key, groups.findIndex((g) => g.key === key) + delta);
  };
  const onSegmentMoveKey = (e: KeyboardEvent, tab: AppTabModel) => {
    const delta = moveDelta(e);
    if (!delta) return;
    e.preventDefault();
    e.stopPropagation();
    const group = groupOf(groups, tab.id);
    if (!group) return;
    refocus.current = { tab: tab.id };
    if (group.tabs.length > 1) moveTab(tab.id, group.tabs.indexOf(tab) + delta);
    else moveGroup(group.key, groups.indexOf(group) + delta);
  };

  // Back steps up to the page's parent when the session has no history.
  const crumbs = header?.crumbs ?? [];
  const crumbParent = crumbs
    .slice(0, -1)
    .reverse()
    .find((c) => c.to && !c.current)?.to;
  const fallbackBackTo = regForActive?.fallbackBackTo ?? crumbParent;

  const isLawyer = userType === "Lawyer";

  return (
    <TooltipProvider delayDuration={300}>
      <header
        data-slot="app-tab-bar"
        {...barProps}
        className={cn(
          "flex h-9 w-full shrink-0 items-center gap-0.5 border-b border-line-2 bg-chrome px-2",
          className
        )}
      >
        {regForActive?.left ? (
          <>
            {regForActive.left}
            <HeaderDivider />
          </>
        ) : null}
        {focused ? null : (
          <>
            <HistoryNav fallbackBackTo={fallbackBackTo} />
            <HeaderDivider />
          </>
        )}
        <div
          ref={stripRef}
          role="tablist"
          aria-label="Open tabs"
          onClickCapture={swallowDropClick}
          className="relative flex min-w-0 flex-1 items-center gap-1 overflow-hidden"
        >
          <DndContext
            sensors={sensors}
            collisionDetection={sameListCenter}
            modifiers={DRAG_MODIFIERS}
            onDragStart={() => setDragging(true)}
            onDragCancel={endDrag}
            onDragEnd={onDragEnd}
          >
            <SortableContext
              items={sortableKeys}
              strategy={horizontalListSortingStrategy}
            >
              {visibleGroups.map((group) => {
                if (group.key === openKey || group.key === shownLingering) {
                  const open = group.key === openKey;
                  return (
                    <TabGroup
                      key={group.key}
                      group={group}
                      mode={open ? "open" : "lingering"}
                      activeId={activeId}
                      compact={layout.compactOpen}
                      maxWidth={layout.openMax}
                      signalsFor={signalsFor}
                      isRunning={isRunning}
                      onSelect={select}
                      onClose={closeTab}
                      onKeep={keepTab}
                      onMoveKey={onSegmentMoveKey}
                      renderDetails={
                        open ? regForActive?.renderDetails : undefined
                      }
                    />
                  );
                }
                const state = groupState(group, signalsFor, isRunning);
                return (
                  <GroupHoverCard
                    key={group.key}
                    group={group}
                    signalsFor={signalsFor}
                    isRunning={isRunning}
                    onSelect={select}
                    onCloseGroup={() => closeGroup(group)}
                    disabled={dragging}
                  >
                    <GroupTile
                      group={group}
                      signals={state.signals}
                      running={state.running}
                      onClick={() => select(lastUsedTab(group, recency))}
                      onKeyDown={(e) => onGroupMoveKey(e, group.key)}
                    />
                  </GroupHoverCard>
                );
              })}
            </SortableContext>
          </DndContext>
          {hidden.length ? (
            <OverflowMenu
              hidden={hidden}
              recency={recency}
              signalsFor={signalsFor}
              isRunning={isRunning}
              onSelect={select}
              onCloseGroup={closeGroup}
            />
          ) : null}
          <div className="flex shrink-0 items-center">
            {isLawyer ? (
              <>
                <NewCaseDialogHost />
                <NewClientDialogHost />
              </>
            ) : null}
            {focused ? null : (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    aria-label="Search (⌘K)"
                    onClick={() => openSearch(true)}
                    className="h-7 gap-1.5 px-1.5 text-fg-3 hover:text-fg"
                  >
                    <Search className="size-4" />
                    <Kbd>⌘K</Kbd>
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="bottom">Search</TooltipContent>
              </Tooltip>
            )}
          </div>
        </div>
        {regForActive?.actions}
        {agent ? (
          <>
            <HeaderDivider />
            <AssistantCue key={agent} agent={agent} />
          </>
        ) : null}
        {regForActive?.right ? (
          <>
            <HeaderDivider />
            {regForActive.right}
          </>
        ) : null}
      </header>
    </TooltipProvider>
  );
};
